import React, { useCallback, useRef } from "react";
import { Grid, Box } from "@mui/material";
import { StriimTypography, StriimDataTable } from "@striim/striim-ui";

import { SentinelsFilter, ActionsFilter, IdentifiersFilter, ImportanceFilter } from "./sentinel-table-filters";
import { styles } from "./sentinel-table.styles";
import StriimCircularProgress from "../../../../apps/pages/manage-striim/connection-profiles/components/circular-progress/circular-progress";
import { GetRowIdParams } from "ag-grid-community";

const ACTIONS = {
    encrypted: "ENCRYPT",
    masked: "MASK",
    noAction: "NO_ACTION"
};

const SentinelTableCommon = ({
    tableColumns,
    title,
    tableData,
    showSentinelsFilter = false,
    showActionsFilter = false,
    showIdentifiersFilter = false,
    showImportanceFilter = false,
    headerHeight = 48,
    manyRowsHeader = false,
    maxHeight = 400,
    customFilterName = undefined,
    showProgress = false
}) => {
    const gridRef = useRef();

    const [filters, setFilters] = React.useState({
        importance: "",
        identifiers: "",
        actions: "",
        sentinels: ""
    });

    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const filteredData = React.useMemo(() => {
        return tableData.filter(item => {
            return (
                (filters.importance === "" ||
                    item.importance?.toLowerCase().includes(filters.importance.toLowerCase())) &&
                (filters.identifiers === "" || item.label?.toLowerCase().includes(filters.identifiers.toLowerCase())) &&
                (filters.actions === "" || item[ACTIONS[filters.actions]] > 0) &&
                (filters.sentinels === "" || item.name?.toLowerCase().includes(filters.sentinels.toLowerCase()))
            );
        });
    }, [tableData, filters]);

    const getRowId = useCallback(
        (params: GetRowIdParams) => {
            const rowID =
                params.data.name && params.data.namespace
                    ? `${params.data.name}.${params.data.namespace}`
                    : params.data.name
                    ? params.data.name
                    : params.data.value;
            return String(rowID);
        },
        [tableData, filteredData]
    );

    return (
        <Grid container>
            <Grid item xs={12} sx={styles.headingContainer}>
                <Box display="flex">
                    <StriimTypography variant="h4" display="inline" sx={{ marginRight: "8px !important" }}>
                        {title}
                    </StriimTypography>
                    {showProgress && <StriimCircularProgress size={16} />}
                </Box>

                {!showProgress && (
                    <Box sx={styles.filtersWrapper}>
                        {showImportanceFilter && <ImportanceFilter updateFilter={updateFilter} />}

                        {showIdentifiersFilter && (
                            <IdentifiersFilter tableData={tableData} updateFilter={updateFilter} />
                        )}

                        {showActionsFilter && <ActionsFilter updateFilter={updateFilter} />}

                        {showSentinelsFilter && (
                            <SentinelsFilter
                                tableData={tableData}
                                customName={customFilterName}
                                updateFilter={updateFilter}
                            />
                        )}
                    </Box>
                )}
            </Grid>

            <Grid container width="100%">
                <Grid
                    width="100%"
                    maxHeight={maxHeight === null ? undefined : maxHeight}
                    sx={
                        manyRowsHeader
                            ? [
                                  styles.dataTable,
                                  styles.dataTableManyHeaderRows,
                                  !filteredData?.length ? styles.emptyDataTable : null
                              ]
                            : [styles.dataTable, !filteredData?.length ? styles.emptyDataTable : null]
                    }
                >
                    {!showProgress && (
                        <StriimDataTable
                            ref={gridRef}
                            data={filteredData}
                            columns={tableColumns}
                            headerHeight={headerHeight}
                            getRowId={getRowId}
                        />
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default SentinelTableCommon;
